import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Trophy, TrendingUp, ShoppingBag, Gift, DollarSign, FileText, Zap, Users, Award, Clock } from 'lucide-react';
import { Link } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useSellerAchievements } from '@/hooks/useSellerAchievements';
import { AchievementUnlockedModal } from './achievements/AchievementUnlockedModal';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { differenceInDays, endOfMonth } from 'date-fns';

interface SellerRankingCardProps {
  className?: string;
  onRankUpdate?: (rank: number, total: number) => void;
}

interface RankingStats {
  total_ventas: number;
  total_canjes: number;
  total_ahorros: number;
  total_monto: number;
  bronce_sales: number;
  plata_sales: number;
  oro_sales: number;
  membership_sale_points: number;
  redemption_points: number;
  passive_redemption_points: number;
  savings_points: number;
  cycle_milestone_points: number;
  achievement_points: number;
  total_score: number;
  training_completed: boolean;
}

export const SellerRankingCard: React.FC<SellerRankingCardProps> = ({ className, onRankUpdate }) => {
  const { user } = useAuth();
  const [ranking, setRanking] = useState<number | null>(null);
  const [totalSellers, setTotalSellers] = useState<number>(0);
  const [stats, setStats] = useState<RankingStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [daysRemaining, setDaysRemaining] = useState<number>(0);
  
  const { 
    achievements, 
    totalPoints, 
    newAchievement, 
    dismissNewAchievement,
    getAchievementBadges,
  } = useSellerAchievements();

  useEffect(() => {
    if (user) {
      fetchRanking();
      calculateDaysRemaining();
    }
  }, [user]);

  const calculateDaysRemaining = () => {
    const today = new Date();
    const monthEnd = endOfMonth(today);
    setDaysRemaining(differenceInDays(monthEnd, today));
  };

  const fetchRanking = async () => {
    if (!user) return;
    
    setLoading(true);
    try {
      // Get total seller count
      const { data: countData } = await (supabase as any).rpc('count_sellers');
      setTotalSellers(countData || 0);

      // Get ranking stats with new fields
      const { data: rankingData } = await (supabase as any).rpc('get_seller_ranking_stats');
      
      if (rankingData && Array.isArray(rankingData)) {
        // Find current user's rank (array is already sorted by total_score)
        const myRankIndex = rankingData.findIndex((s: any) => s.seller_id === user.id);
        
        if (myRankIndex >= 0) {
          const newRank = myRankIndex + 1;
          setRanking(newRank);
          
          // Notify parent about rank update for synchronization
          onRankUpdate?.(newRank, countData || 0);
          
          const myStats = rankingData[myRankIndex];
          setStats({
            total_ventas: myStats.total_ventas || 0,
            total_canjes: myStats.total_canjes || 0,
            total_ahorros: myStats.total_ahorros || 0,
            total_monto: myStats.total_monto || 0,
            bronce_sales: myStats.bronce_sales || 0,
            plata_sales: myStats.plata_sales || 0,
            oro_sales: myStats.oro_sales || 0,
            membership_sale_points: myStats.membership_sale_points || 0,
            redemption_points: myStats.redemption_points || 0,
            passive_redemption_points: myStats.passive_redemption_points || 0,
            savings_points: myStats.savings_points || 0,
            cycle_milestone_points: myStats.cycle_milestone_points || 0,
            achievement_points: myStats.achievement_points || 0,
            total_score: myStats.total_score || 0,
            training_completed: myStats.training_completed || false,
          });
        }
      }
    } catch (err) {
      console.error('Error fetching ranking:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className={`bg-white/5 backdrop-blur-xl rounded-2xl p-6 border border-white/10 ${className}`}>
        <div className="animate-pulse flex items-center gap-4">
          <div className="w-16 h-16 rounded-full bg-white/10" />
          <div className="flex-1 space-y-2">
            <div className="h-4 bg-white/10 rounded w-24" />
            <div className="h-6 bg-white/10 rounded w-32" />
          </div>
        </div>
      </div>
    );
  }

  if (ranking === null) {
    return null;
  }

  const getRankColor = (rank: number) => {
    if (rank === 1) return { bg: 'from-yellow-500/30 to-amber-600/20', border: 'border-yellow-500/50', icon: 'text-yellow-400' };
    if (rank === 2) return { bg: 'from-slate-400/30 to-slate-500/20', border: 'border-slate-400/50', icon: 'text-slate-300' };
    if (rank === 3) return { bg: 'from-amber-700/30 to-orange-800/20', border: 'border-amber-600/50', icon: 'text-amber-500' };
    return { bg: 'from-blue-500/20 to-blue-600/10', border: 'border-blue-500/30', icon: 'text-blue-400' };
  };

  const colors = getRankColor(ranking);

  const formatMoney = (amount: number) => {
    if (amount >= 1000000) {
      return `₡${(amount / 1000000).toFixed(1)}M`;
    }
    if (amount >= 1000) {
      return `₡${(amount / 1000).toFixed(0)}k`;
    }
    return `₡${amount}`;
  };

  const totalMembershipSales = (stats?.bronce_sales || 0) + (stats?.plata_sales || 0) + (stats?.oro_sales || 0);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className={`bg-gradient-to-br ${colors.bg} backdrop-blur-xl rounded-2xl p-5 border ${colors.border} ${className}`}
    >
      <div className="flex items-center gap-4">
        {/* Rank Badge */}
        <div className="relative">
          <div className={`w-16 h-16 rounded-full bg-black/30 flex items-center justify-center border-2 ${colors.border}`}>
            {ranking <= 3 ? (
              <Trophy className={`w-7 h-7 ${colors.icon}`} />
            ) : (
              <span className={`text-2xl font-bold ${colors.icon}`}>#{ranking}</span>
            )}
          </div>
          {ranking <= 3 && (
            <span className={`absolute -bottom-1 -right-1 w-7 h-7 rounded-full bg-black flex items-center justify-center text-sm font-bold ${colors.icon} border ${colors.border}`}>
              {ranking}
            </span>
          )}
        </div>

        {/* Stats */}
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <TrendingUp className={`w-4 h-4 ${colors.icon}`} />
            <span className="text-white/60 text-sm">Tu ranking este mes</span>
          </div>
          <p className="text-white text-xl font-bold">
            #{ranking} <span className="text-white/40 text-sm font-normal">de {totalSellers}</span>
          </p>
        </div>

        {/* Days remaining */}
        <div className="text-right">
          <div className="flex items-center gap-1 text-white/50 text-xs">
            <Clock className="w-3 h-3" />
            <span>Quedan</span>
          </div>
          <p className="text-white font-bold">{daysRemaining} días</p>
        </div>
      </div>

      {/* Score Total - New Contest System */}
      {stats && (
        <TooltipProvider>
          <div className="mt-4">
            {/* Main Score card */}
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="bg-gradient-to-r from-amber-500/20 to-yellow-500/10 rounded-xl p-4 border border-amber-500/30 cursor-help">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <Zap className="w-5 h-5 text-amber-400" />
                      <span className="text-white/80 text-sm font-medium">Score Total</span>
                    </div>
                    <span className="text-amber-400 font-bold text-2xl">
                      {stats.total_score} pts
                    </span>
                  </div>
                  
                  {/* Score breakdown - NEW SYSTEM */}
                  <div className="grid grid-cols-3 gap-1 mt-3">
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div className="text-center p-2 bg-black/20 rounded-lg cursor-help hover:bg-black/30 transition-colors">
                          <p className="text-blue-400 font-semibold text-sm">+{stats.membership_sale_points}</p>
                          <p className="text-white/40 text-[9px]">Membresías</p>
                        </div>
                      </TooltipTrigger>
                      <TooltipContent className="bg-zinc-900 border-white/10">
                        <div className="text-xs space-y-1">
                          <p className="font-medium text-white mb-1">Desglose:</p>
                          <p className="text-amber-600">🥉 Bronce: {stats.bronce_sales} × 10 = {stats.bronce_sales * 10}</p>
                          <p className="text-slate-300">🥈 Plata: {stats.plata_sales} × 20 = {stats.plata_sales * 20}</p>
                          <p className="text-yellow-400">🥇 Oro: {stats.oro_sales} × 40 = {stats.oro_sales * 40}</p>
                        </div>
                      </TooltipContent>
                    </Tooltip>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div className="text-center p-2 bg-black/20 rounded-lg cursor-help hover:bg-black/30 transition-colors">
                          <p className="text-purple-400 font-semibold text-sm">+{stats.redemption_points}</p>
                          <p className="text-white/40 text-[9px]">Canjes ×8</p>
                        </div>
                      </TooltipTrigger>
                      <TooltipContent className="bg-zinc-900 border-white/10">
                        <div className="text-xs">
                          <p className="text-white">{stats.total_canjes} canjes × 8 pts = {stats.redemption_points} pts</p>
                        </div>
                      </TooltipContent>
                    </Tooltip>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div className="text-center p-2 bg-black/20 rounded-lg cursor-help hover:bg-black/30 transition-colors">
                          <p className="text-green-400 font-semibold text-sm">+{stats.passive_redemption_points}</p>
                          <p className="text-white/40 text-[9px]">Pasivos ×2</p>
                        </div>
                      </TooltipTrigger>
                      <TooltipContent className="bg-zinc-900 border-white/10">
                        <div className="text-xs">
                          <p className="text-white">Canjes en membresías que vendiste</p>
                          <p className="text-white/60">Ganas 2 pts por cada canje</p>
                        </div>
                      </TooltipContent>
                    </Tooltip>
                  </div>
                  <div className="grid grid-cols-3 gap-1 mt-1">
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div className="text-center p-2 bg-black/20 rounded-lg cursor-help hover:bg-black/30 transition-colors">
                          <p className="text-emerald-400 font-semibold text-sm">+{stats.savings_points}</p>
                          <p className="text-emerald-300/50 text-[8px]">{formatMoney(stats.total_ahorros)}</p>
                          <p className="text-white/40 text-[9px]">Ahorros</p>
                        </div>
                      </TooltipTrigger>
                      <TooltipContent className="bg-zinc-900 border-white/10">
                        <div className="text-xs">
                          <p className="text-emerald-400 font-medium">{formatMoney(stats.total_ahorros)} ahorrados</p>
                          <p className="text-white/60">÷ 10,000 × 10 = {stats.savings_points} pts</p>
                        </div>
                      </TooltipContent>
                    </Tooltip>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div className="text-center p-2 bg-black/20 rounded-lg cursor-help hover:bg-black/30 transition-colors">
                          <p className="text-orange-400 font-semibold text-sm">+{stats.cycle_milestone_points}</p>
                          <p className="text-white/40 text-[9px]">Hitos Ciclo</p>
                        </div>
                      </TooltipTrigger>
                      <TooltipContent className="bg-zinc-900 border-white/10">
                        <div className="text-xs">
                          <p className="text-white font-medium mb-1">Hitos del Ciclo</p>
                          <p className="text-white/60">Bonos mensuales que se reinician cada mes:</p>
                          <p className="text-white/50 text-[10px] mt-1">• Primera venta, racha, etc.</p>
                        </div>
                      </TooltipContent>
                    </Tooltip>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div className="text-center p-2 bg-black/20 rounded-lg cursor-help hover:bg-black/30 transition-colors">
                          <p className="text-amber-400 font-semibold text-sm">+{totalPoints}</p>
                          <p className="text-white/40 text-[9px]">🏆 Logros Perm.</p>
                        </div>
                      </TooltipTrigger>
                      <TooltipContent className="bg-zinc-900 border-white/10">
                        <div className="text-xs">
                          <p className="text-white font-medium mb-1">Logros Permanentes</p>
                          <p className="text-white/60">Logros por tu trayectoria total.</p>
                          <p className="text-white/50 text-[10px] mt-1">Nunca se pierden.</p>
                        </div>
                      </TooltipContent>
                    </Tooltip>
                  </div>
                </div>
              </TooltipTrigger>
              <TooltipContent className="bg-zinc-900 border-white/10 max-w-xs">
                <div className="text-white text-xs space-y-1">
                  <p className="font-medium mb-2">Fórmula del Score:</p>
                  <p>• Membresías: B×10 + P×20 + O×40</p>
                  <p>• Canjes procesados: ×8 pts</p>
                  <p>• Canjes pasivos: ×2 pts</p>
                  <p>• Ahorro: 10 pts / ₡10,000</p>
                  <p>• Hitos del ciclo + Logros</p>
                </div>
              </TooltipContent>
            </Tooltip>

            {/* Membership Sales Breakdown */}
            <div className="mt-3 bg-black/20 rounded-xl p-3">
              <p className="text-white/50 text-xs mb-2 flex items-center gap-1">
                <ShoppingBag className="w-3 h-3" />
                Membresías vendidas este mes
              </p>
              <div className="grid grid-cols-3 gap-2">
                <div className="text-center">
                  <span className="text-amber-600 font-bold">{stats.bronce_sales}</span>
                  <p className="text-white/40 text-[10px]">Bronce</p>
                </div>
                <div className="text-center">
                  <span className="text-slate-300 font-bold">{stats.plata_sales}</span>
                  <p className="text-white/40 text-[10px]">Plata</p>
                </div>
                <div className="text-center">
                  <span className="text-yellow-400 font-bold">{stats.oro_sales}</span>
                  <p className="text-white/40 text-[10px]">Oro</p>
                </div>
              </div>
            </div>

            {/* Other stats */}
            <div className="grid grid-cols-2 gap-3 mt-3">
              <div className="bg-black/20 rounded-xl p-3 text-center">
                <Gift className="w-4 h-4 text-purple-400 mx-auto mb-1" />
                <p className="text-white font-bold text-lg">{stats.total_canjes}</p>
                <p className="text-white/40 text-[10px]">Canjes</p>
              </div>
              <div className="bg-black/20 rounded-xl p-3 text-center">
                <DollarSign className="w-4 h-4 text-green-400 mx-auto mb-1" />
                <p className="text-white font-bold text-lg">{formatMoney(stats.total_ahorros)}</p>
                <p className="text-white/40 text-[10px]">Ahorros</p>
              </div>
            </div>
          </div>
        </TooltipProvider>
      )}

      {/* Achievement Badges */}
      {achievements.length > 0 && (
        <div className="mt-4 pt-3 border-t border-white/10">
          <p className="text-white/50 text-xs mb-2">Mis Logros ({achievements.length})</p>
          <div className="flex flex-wrap gap-1">
            {getAchievementBadges().slice(0, 6).map((badge, i) => (
              <span key={i} className="text-lg">{badge}</span>
            ))}
            {achievements.length > 6 && (
              <span className="text-white/40 text-sm">+{achievements.length - 6}</span>
            )}
          </div>
        </div>
      )}

      {/* Link to Incentive Program */}
      <div className="mt-4 pt-3 border-t border-white/10">
        <Link
          to="/programa-incentivos"
          className="flex items-center justify-center gap-2 w-full py-2 px-3 text-sm font-medium text-blue-400 bg-blue-500/10 hover:bg-blue-500/20 border border-blue-500/30 rounded-lg transition-colors"
        >
          <FileText className="w-4 h-4" />
          Ver Reglas del Juego
        </Link>
      </div>

      {/* Achievement Modal */}
      <AchievementUnlockedModal 
        achievementType={newAchievement} 
        onClose={dismissNewAchievement} 
      />
    </motion.div>
  );
};

export default SellerRankingCard;
